import { Hint, Tooltip, centsToUnits } from '@systemeio/ui'
import StatisticsTabTableLayout from 'modules/funnels/funnel/components/statistics-tab/statistics-tab-table-layout'
import TabTable, { TabColumnDefinitionInterface } from 'modules/funnels/funnel/components/tab-table'
import { useFunnelStatistics } from 'modules/funnels/funnel/hooks/use-funnel-statistics'
import {
  FunnelStatisticsFilterInterface,
  FunnelStatisticsInterface,
  FunnelStepStatisticsInterface,
} from 'modules/funnels/funnel/types/funnel-statistics-interface'
import { FunnelStepInterface } from 'modules/funnels/funnel/types/funnel-step-interface'
import { FunnelId } from 'modules/funnels/types/funnel-interface'
import React, { useState } from 'react'
import { Loader } from 'shared/components/loader'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { currencyFormat } from 'shared/utils/currency-format'

interface SalesTabProps {
  funnelId: FunnelId
  funnelStep: FunnelStepInterface | null
}

const defaultFilters: FunnelStatisticsFilterInterface = { date: [null, null] }

function StatisticsTab({ funnelId }: SalesTabProps) {
  const { t } = useLocoTranslation()
  const { user } = useUser()
  const [filter, setFilter] = useState<FunnelStatisticsFilterInterface>(defaultFilters)
  const { statistics } = useFunnelStatistics({ funnelId, filter })

  if (!statistics) {
    return (
      <div className={'flex h-full items-center justify-center py-12'}>
        <Loader className={'scale-150'} />
      </div>
    )
  }

  const columns: TabColumnDefinitionInterface<
    FunnelStepStatisticsInterface,
    FunnelStatisticsInterface
  >[] = [
    {
      key: 'funnelStepName',
      header: ' ',
      widthPercent: 15,
      subHeaders: [
        {
          widthPercent: 100,
          title: ' ',
          onRender: funnelStep => (
            <span className="group:justify-start truncate">{funnelStep.name}</span>
          ),
          onRenderSmall: funnelStep => (
            <span className="font-inter text-text-primary truncate text-base font-semibold">
              {funnelStep.name}
            </span>
          ),
          align: 'justify-start',
        },
      ],
    },
    {
      key: 'funnelStepViews',
      header: t('dashboard.funnel.statistics.page_views'),
      widthPercent: 7,
      subHeaders: [
        {
          widthPercent: 100,
          title: ' ',
          onRender: funnelStep => <span className="truncate">{funnelStep.pageViewsUnique}</span>,
        },
      ],
    },
    {
      key: 'optIns',
      header: (
        <Hint label={t('dashboard.funnel.statistics.opt_in_title_hint')}>
          {t('dashboard.funnel.statistics.opt_in_title')}
        </Hint>
      ),
      widthPercent: 20,
      subHeaders: [
        {
          widthPercent: 30,
          title: t('dashboard.funnel.statistics.all'),
          onRender: funnelStep => (
            <span className="truncate font-normal">{funnelStep.optInAll}</span>
          ),
        },
        {
          widthPercent: 70,
          title: t('dashboard.funnel.statistics.conversion_rate'),
          onRender: funnelStep => (
            <span className="truncate font-normal">{funnelStep.optInRate}%</span>
          ),
        },
      ],
    },
    {
      key: 'sales',
      header: t('dashboard.funnel.statistics.sales'),
      widthPercent: 40,
      subHeaders: [
        {
          widthPercent: 20,
          title: t('dashboard.funnel.statistics.total'),
          onRender: funnelStep => (
            <span className="truncate font-normal">{funnelStep.salesCount || '-'}</span>
          ),
        },
        {
          widthPercent: 30,
          title: t('dashboard.funnel.statistics.conversion_rate'),
          onRender: funnelStep => (
            <span className="truncate font-normal">
              {funnelStep.salesRate ? `${funnelStep.salesRate}%` : '-'}
            </span>
          ),
        },
        {
          widthPercent: 50,
          title: t('dashboard.funnel.statistics.revenue'),
          onRender: funnelStep => (
            <Tooltip
              mode="overflow"
              label={
                <>
                  {funnelStep.salesGrossSum
                    ? currencyFormat(
                        centsToUnits(funnelStep.salesGrossSum),
                        statistics.currency,
                        user?.dashboardLocale,
                      )
                    : '-'}
                </>
              }
              wrapperClassName="flex w-full h-full justify-center align-center"
              className="flex"
            >
              <span className="truncate font-normal">
                {funnelStep.salesGrossSum
                  ? currencyFormat(
                      centsToUnits(funnelStep.salesGrossSum),
                      statistics.currency,
                      user?.dashboardLocale,
                    )
                  : '-'}
              </span>
            </Tooltip>
          ),
        },
      ],
    },
    {
      key: 'earning/preview',
      header: t('dashboard.funnel.statistics.earning_pageview_title'),
      widthPercent: 12,
      subHeaders: [
        {
          widthPercent: 100,
          title: ' ',
          onRender: funnelStep => (
            <Tooltip
              mode="overflow"
              label={
                <>
                  {funnelStep.earningPerViewUnique
                    ? currencyFormat(
                        centsToUnits(funnelStep.earningPerViewUnique),
                        statistics.currency,
                        user?.dashboardLocale,
                      )
                    : '-'}
                </>
              }
              wrapperClassName="flex w-full h-full justify-center align-center"
              className="flex"
            >
              <span className="truncate font-normal">
                {funnelStep.earningPerViewUnique
                  ? currencyFormat(
                      centsToUnits(funnelStep.earningPerViewUnique),
                      statistics.currency,
                      user?.dashboardLocale,
                    )
                  : '-'}
              </span>
            </Tooltip>
          ),
        },
      ],
    },
  ]

  return (
    <StatisticsTabTableLayout
      filter={filter}
      setFilter={setFilter}
      currency={statistics.currency}
      totalSales={statistics.commonGross}
      averageCartValue={statistics.commonAverageInOrder}
    >
      <TabTable columns={columns} data={statistics} />
    </StatisticsTabTableLayout>
  )
}

export default StatisticsTab
